// src/data/radarClient.js
//
// LEADERSHIP-RADAR-S1: Data layer for the Leadership Radar feed.
//
//   getRadarFeed({ includeResolved, sort })  — also accepts legacy { resolved }
//     → { items: RankedItem[], counts: ChipCounts }
//
//   patchRadarTriage(itemKey, patch)
//     → { item: TriageRow }

/**
 * @typedef {object} RadarItem
 * @property {string}       itemKey
 * @property {string}       type          - "decision" | "action" | "signal" | "expiry" | "issue" | "project"
 * @property {string}       title
 * @property {string|null}  locationName
 * @property {string|null}  locationId
 * @property {string|null}  owner
 * @property {string|null}  dueDate
 * @property {string|null}  recommendedMove
 * @property {string|null}  ifDelayed
 * @property {string|null}  navUrl
 * @property {string|null}  playbookRunId - non-null when the action was emitted by a playbook check
 * @property {string|null}  sourceTag     - "playbook" when playbookRunId is set
 * @property {boolean}      _seeded
 */

export async function getRadarFeed({ includeResolved = false, resolved = false, sort = null } = {}) {
  const params = new URLSearchParams();
  if (includeResolved || resolved) params.set("includeResolved", "1");
  if (sort) params.set("sort", sort);
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`/api/radar/feed${qs}`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Radar feed fetch failed: ${res.status}`);
  }
  return res.json();
}

export async function deleteRadarItem(itemKey) {
  const res = await fetch(`/api/radar/${encodeURIComponent(itemKey)}`, {
    method:      "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Radar delete failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  // 204 No Content — no body to parse
}

// ─── Admin radar settings ─────────────────────────────────────────────────────

/**
 * Get all four radar config groups from the admin settings endpoint.
 * Returns { timing, emitters, rankingWeights, bands }.
 */
export async function getRadarSettings() {
  const res = await fetch("/api/admin/radar-settings", { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Radar settings fetch failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Patch one or more radar config groups.
 * @param {object} patch - subset of { timing, emitters, rankingWeights, bands }
 */
export async function patchRadarSettings(patch) {
  const res = await fetch("/api/admin/radar-settings", {
    method:      "PATCH",
    headers:     { "Content-Type": "application/json" },
    credentials: "include",
    body:        JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Radar settings PATCH failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function patchRadarTriage(itemKey, patch) {
  const res = await fetch(`/api/radar/${encodeURIComponent(itemKey)}/triage`, {
    method:      "PATCH",
    headers:     { "Content-Type": "application/json" },
    credentials: "include",
    body:        JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Triage PATCH failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
